import React, { useState, useEffect } from 'react'
import { Box, BoxProps, TextProps } from '@chakra-ui/react'
import {
    NumberInput,
    NumberInputProps,
    NumberInputField,
    NumberInputStepper,
    NumberIncrementStepper,
    NumberDecrementStepper,
} from '@chakra-ui/react'
import SharedLabel from './shared-label'
import { FiChevronDown, FiChevronUp } from 'react-icons/fi'
import { NumberInputFieldProps } from '@chakra-ui/react'

interface Props extends Omit<NumberInputProps, 'onChange' | 'value'> {
    value?: number
    onChange?: (valueAsString: string, valueAsNumber: number) => void
    label?: string
    labelProps?: TextProps
    containerProps?: BoxProps
    showStepper?: boolean
    inputFieldProps?: NumberInputFieldProps
    allowFloatIntermediate?: boolean
}

const SharedNumberInput: React.FC<Props> = ({
    label,
    labelProps,
    containerProps,
    showStepper,
    inputFieldProps,
    allowFloatIntermediate,
    value: externalValue,
    onChange: externalOnChange,
    onBlur: externalOnBlur,
    ...props
}) => {
    const [internalInputValue, setInternalInputValue] = useState<string>(
        String(externalValue ?? 0),
    )

    useEffect(() => {
        if (
            allowFloatIntermediate ||
            String(externalValue ?? 0) !== internalInputValue
        ) {
            if (
                parseFloat(internalInputValue) !== (externalValue ?? 0) ||
                !allowFloatIntermediate
            ) {
                setInternalInputValue(String(externalValue ?? 0))
            }
        }
    }, [externalValue, allowFloatIntermediate, internalInputValue])

    const handleChange = (valueAsString: string, valueAsNumber: number) => {
        setInternalInputValue(valueAsString)
        if (externalOnChange) {
            externalOnChange(valueAsString, valueAsNumber)
        }
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const parsed = parseFloat(internalInputValue)
        if (isNaN(parsed)) {
            setInternalInputValue('0')
            if (externalOnChange) {
                externalOnChange('0', 0)
            }
        } else if (String(parsed) !== internalInputValue) {
            setInternalInputValue(String(parsed))
            if (externalOnChange) {
                externalOnChange(String(parsed), parsed)
            }
        }

        if (externalOnBlur) {
            externalOnBlur(event)
        }
    }

    const numberInputProps = allowFloatIntermediate
        ? {
            value: internalInputValue,
            onChange: handleChange,
            onBlur: handleBlur,
        }
        : {
            value: externalValue,
            onChange: externalOnChange,
            onBlur: externalOnBlur,
        }

    return (
        <Box {...containerProps}>
            {label && (
                <SharedLabel mb={2} {...labelProps}>
                    {label}
                </SharedLabel>
            )}
            <NumberInput
                focusBorderColor="brand.400"
                size="md"
                {...props}
                {...numberInputProps}
            >
                <NumberInputField {...inputFieldProps} />
                {showStepper && (
                    <NumberInputStepper>
                        <NumberIncrementStepper children={<FiChevronUp />} />
                        <NumberDecrementStepper children={<FiChevronDown />} />
                    </NumberInputStepper>
                )}
            </NumberInput>
        </Box>
    )
}

export default SharedNumberInput
